import sortBy from 'lodash-es/sortBy';
import { type ForwardedRef, forwardRef, useEffect, useMemo } from 'react';

import {
  type ListDepartmentsQuery,
  type ListDepartmentsQueryVariables,
  useListDepartmentsQuery,
} from '@/common/graphql';

import type { OrgHierarchyRelationItem } from '@/common/utils';
import { isDisabledByRelation } from '@/common/utils';

import type { ComboboxItemWithFormatted, CommonProps, UseQueryReturn } from '../props';
import { SelectMultiple, type SelectMultipleProps } from '../SelectMultiple';
import { SelectSingle, type SelectSingleProps } from '../SelectSingle';

export type DepartmentSelectVariables = Partial<
  Omit<ListDepartmentsQueryVariables, 'pageNumber' | 'pageSize' | 'keyword'>
>;
type Data = ComboboxItemWithFormatted &
  Omit<ListDepartmentsQuery['listDepartments']['items'][number], 'id' | 'name' | 'enabled'>;
type Props = CommonProps<DepartmentSelectVariables> & {
  orgRelations?: OrgHierarchyRelationItem[];
  selectedBusinessUnitIds?: string[];
};

const useQuery = (
  variables: DepartmentSelectVariables | undefined,
  orgRelations: OrgHierarchyRelationItem[] | undefined,
  selectedBusinessUnitIds: string[] | undefined,
  onLoadingCompleted?: () => void,
): UseQueryReturn<ListDepartmentsQueryVariables, Data> => {
  const { data, ...rest } = useListDepartmentsQuery({
    variables: {
      pageNumber: null,
      pageSize: null,
      keyword: null,
      enabled: variables?.enabled ?? null,
      hasEmployees: variables?.hasEmployees ?? null,
      excludeIds: variables?.excludeIds ?? null,
      companyId: variables?.companyId ?? null,
      businessUnitId: variables?.businessUnitId ?? null,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (onLoadingCompleted) onLoadingCompleted();
  }, [onLoadingCompleted]);

  const relations = useMemo(() => orgRelations ?? [], [orgRelations]);
  const unsorted = useMemo(
    () =>
      data?.listDepartments?.items?.map(it => ({
        ...it,
        value: it.id,
        label: it.name,
        disabled:
          !it.enabled ||
          (selectedBusinessUnitIds && selectedBusinessUnitIds.length > 0
            ? selectedBusinessUnitIds.every(buId => isDisabledByRelation(it.id, relations, buId))
            : isDisabledByRelation(it.id, relations, undefined)),
      })) || [],
    [data, relations, selectedBusinessUnitIds],
  );

  return {
    data: sortBy(unsorted, it => it.disabled),
    ...rest,
  };
};

export const DepartmentSelectSingle = forwardRef(
  (props: Props & SelectSingleProps<Data>, ref: ForwardedRef<HTMLDivElement>) => {
    const { data, loading } = useQuery(
      props.variables,
      props.orgRelations,
      props.selectedBusinessUnitIds,
      props.onLoadingCompleted,
    );
    return <SelectSingle ref={ref} {...props} data={data} loading={loading} />;
  },
);

export const DepartmentSelectMultiple = (props: Props & SelectMultipleProps<Data>) => {
  const { data, loading } = useQuery(
    props.variables,
    props.orgRelations,
    props.selectedBusinessUnitIds,
    props.onLoadingCompleted,
  );
  return (
    <SelectMultiple
      multipleSelectedLabel={length => `${length} departments`}
      {...props}
      data={data}
      loading={loading}
    />
  );
};
